import React, { useEffect, useState, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { LatLngBounds } from 'leaflet';
import { geocodeLocation } from '../../services/geocoding';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icon issue with webpack
import L from 'leaflet';

// Use Leaflet's default icon configuration
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface League {
  id: number;
  name: string;
  description: string;
  venue: string;
  city: string;
  country: string;
  teamFormat: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  createdAt: string;
  packages: Array<{
    id: number;
    name: string;
    description: string;
    price: number;
    includes: number[];
  }>;
  squads: Array<{
    id: number;
    name: string;
    date: string;
    startTime: string;
    endTime: string;
    gamesPerSquad: number;
  }>;
  sideEvents: Array<{
    id: number;
    name: string;
    description: string;
    type: string;
    cost: number;
  }>;
}

interface LeagueWithCoords extends League {
  lat: number;
  lng: number;
}

interface LeagueMapProps {
  leagues: League[];
  onLeagueClick: (league: League) => void;
  formatDate: (dateString: string) => string;
  getLeagueStatus: (league: League) => string;
  t: (key: string, options?: any) => string;
}

// Component to adjust map bounds when markers change
const MapBoundsUpdater: React.FC<{ leagues: LeagueWithCoords[] }> = ({ leagues }) => {
  const map = useMap();

  useEffect(() => {
    if (leagues.length > 0) {
      const bounds = new LatLngBounds(
        leagues.map(league => [league.lat, league.lng])
      );
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
    }
  }, [leagues, map]);

  return null;
};

const LeagueMap: React.FC<LeagueMapProps> = ({
  leagues,
  onLeagueClick,
  formatDate,
  getLeagueStatus,
  t
}) => {
  const [leaguesWithCoords, setLeaguesWithCoords] = useState<LeagueWithCoords[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const geocodeLeagues = async () => {
      setLoading(true);
      setError(null);
      
      const geocodedLeagues: LeagueWithCoords[] = [];

      for (const league of leagues) {
        if (!league.city || !league.city.trim()) {
          console.warn(`Skipping league "${league.name}" — missing city`);
          continue;
        }
        try {
          const coords = await geocodeLocation(league.city, undefined, league.country);
          
          if (coords) {
            geocodedLeagues.push({
              ...league,
              lat: coords.lat,
              lng: coords.lng
            });
          } else {
            console.warn(`Could not geocode: ${league.city}, ${league.country}`);
          }
        } catch (err) {
          console.error(`Error geocoding ${league.name}:`, err);
        }
      }

      if (geocodedLeagues.length === 0 && leagues.length > 0) {
        setError(t('player.noLeaguesGeocoded'));
      }

      setLeaguesWithCoords(geocodedLeagues);
      setLoading(false);
    };

    if (leagues.length > 0) {
      geocodeLeagues();
    } else {
      setLeaguesWithCoords([]);
      setLoading(false);
    }
  }, [leagues, t]);

  // Default center (world view)
  const defaultCenter = useMemo(() => ({ lat: 20, lng: 0 }), []);
  const defaultZoom = 2;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[600px] bg-white/10 backdrop-blur-lg rounded-xl border border-white/20">
        <div className="text-center">
          <div className="text-4xl mb-4">🌍</div>
          <p className="text-blue-200 text-lg">{t('player.loadingMap')}</p>
          <p className="text-blue-300 text-sm mt-2">{t('player.geocodingLocations')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[600px] bg-white/10 backdrop-blur-lg rounded-xl border border-white/20">
        <div className="text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <p className="text-red-300 text-lg">{error}</p>
        </div>
      </div>
    );
  }

  if (leaguesWithCoords.length === 0) {
    return (
      <div className="flex items-center justify-center h-[600px] bg-white/10 backdrop-blur-lg rounded-xl border border-white/20">
        <div className="text-center">
          <div className="text-6xl mb-4">🗺️</div>
          <h3 className="text-xl font-bold text-white mb-2">{t('player.noLeaguesOnMap')}</h3>
          <p className="text-blue-200">{t('player.tryAdjustingFilters')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden">
      <MapContainer
        center={[defaultCenter.lat, defaultCenter.lng]}
        zoom={defaultZoom}
        style={{ height: '600px', width: '100%' }}
        className="z-0"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapBoundsUpdater leagues={leaguesWithCoords} />

        {leaguesWithCoords.map((league) => (
          <Marker
            key={league.id}
            position={[league.lat, league.lng]}
          >
            <Popup maxWidth={300}>
              <div className="p-2">
                <h3 className="text-lg font-bold text-gray-800 mb-2">{league.name}</h3>
                
                <div className="space-y-1 text-sm text-gray-700 mb-3">
                  <div>
                    <span className="font-semibold">📍</span> {league.venue}
                  </div>
                  <div>
                    <span className="font-semibold">🌆</span> {league.city}, {league.country}
                  </div>
                  <div>
                    <span className="font-semibold">👥</span> {league.teamFormat}
                  </div>
                  <div>
                    <span className="font-semibold">📅</span> {formatDate(league.startDate)} - {formatDate(league.endDate)}
                  </div>
                  <div>
                    <span className="font-semibold">📊</span> {getLeagueStatus(league)}
                  </div>
                </div>

                {league.packages && league.packages.length > 0 && (
                  <div className="mb-3 pt-2 border-t border-gray-200">
                    <div className="font-semibold text-xs text-gray-600 mb-1">
                      {t('player.packagesAvailable')}:
                    </div>
                    {league.packages.slice(0, 2).map((pkg) => (
                      <div key={pkg.id} className="text-xs text-gray-600 flex justify-between">
                        <span>{pkg.name}</span>
                        <span className="font-semibold">${pkg.price}</span>
                      </div>
                    ))}
                    {league.packages.length > 2 && (
                      <div className="text-xs text-gray-500 italic">
                        +{league.packages.length - 2} {t('player.more')}
                      </div>
                    )}
                  </div>
                )}

                <button
                  onClick={() => onLeagueClick(league)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded transition duration-200 text-sm"
                >
                  {t('player.viewDetails')}
                </button>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default LeagueMap;
